import { useEffect, useState } from 'react';
import { Link2 } from 'lucide-react';
import { apiFetch, v1Path } from '../../lib/api';
import { INITIATIVE_PROJECT_IDENTIFIER, isRequirementKey } from '../../lib/initiatives';
import { patchTask } from '../../lib/requirement-card';
import type { WorkItemListDecorations, WorkItemSlim } from '../../lib/work-types';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useOverlays } from './overlay-context';

type Row = WorkItemSlim & WorkItemListDecorations;

const SEARCH_LIMIT = 20;

/** Stream tasks that can become a launch step: top-level tasks not already on this initiative. */
export function linkableTasks(rows: Row[], initiativeId: string): Row[] {
  return rows.filter(
    (row) =>
      row.type === 'task' &&
      row.parent_id === null &&
      !row.archived_at &&
      row.initiative_id !== initiativeId &&
      !isRequirementKey(row.key) &&
      !row.key.startsWith(`${INITIATIVE_PROJECT_IDENTIFIER}-`),
  );
}

/** Picks a task that already exists in a stream and links it to the initiative as a launch step. */
export default function LinkExistingStep({ initiativeId, initiativeKey }: { initiativeId: string; initiativeKey: string }) {
  const { toast, confirm } = useOverlays();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Row[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const trimmed = query.trim();
    if (!trimmed) {
      setResults(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({
        query: trimmed,
        types: 'task',
        show_sub_items: 'false',
        limit: String(SEARCH_LIMIT),
        offset: '0',
      });
      apiFetch<{ items: Row[] }>(`${v1Path('/work-items')}?${params.toString()}`)
        .then((res) => { if (!cancelled) setResults(linkableTasks(res.items ?? [], initiativeId)); })
        .catch(() => { if (!cancelled) setResults([]); });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, query, initiativeId]);

  async function link(row: Row) {
    if (row.initiative_id && row.initiative) {
      const ok = await confirm({
        title: 'Move to this initiative',
        description: `${row.key} is a launch step of ${row.initiative.key} ${row.initiative.title}. Move it to ${initiativeKey}?`,
        confirmLabel: 'Move',
      });
      if (!ok) return;
    }
    setBusyId(row.id);
    try {
      await patchTask(row.project_id, row.id, { initiative_id: initiativeId });
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
      toast(`Linked ${row.key}`);
      setOpen(false);
      setQuery('');
      setResults(null);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not link the task');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="outline">
          <Link2 className="h-4 w-4" aria-hidden />
          Link existing task
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 p-3" aria-label="Link an existing task">
        <Input
          autoFocus
          className="h-8 text-sm"
          placeholder="Search by key or title, e.g. SALES-12"
          aria-label="Search tasks"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className="mt-2 max-h-72 overflow-y-auto">
          {results === null ? (
            <p className="px-1 py-2 text-xs text-muted-foreground">Type to find a task in the streams you belong to.</p>
          ) : results.length === 0 ? (
            <p className="px-1 py-2 text-xs text-muted-foreground">No matching tasks.</p>
          ) : (
            <ul className="divide-y">
              {results.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    disabled={busyId !== null}
                    onClick={() => void link(row)}
                    className="flex w-full items-baseline gap-2 px-1 py-2 text-left hover:bg-muted/50 disabled:opacity-60"
                  >
                    <span className="shrink-0 font-mono text-xs text-muted-foreground">{row.key}</span>
                    <span className="min-w-0 flex-1 truncate text-sm">{row.title}</span>
                    {row.initiative ? (
                      <span className="shrink-0 text-xs text-muted-foreground">on {row.initiative.key}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
